/**
 * Phase 14.12 — Campaigns User Guide help page.
 *
 * Renders the markdown source served from
 * ``GET /api/docs/campaigns-user-guide?format=markdown`` via
 * react-markdown + remark-gfm (tables / strikethrough / task lists).
 * Operators reach this page via the Help affordance in the
 * Campaign Builder header (WizardShell) or directly by URL.
 *
 * The PDF download mirrors the existing campaign report download
 * pattern — axios blob via the auth interceptor, then
 * ``URL.createObjectURL`` + temp anchor click + revoke.
 */
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Download, FileText } from "lucide-react";
import client from "@/api/client";
import { Button } from "@/components/ui/button";

export default function CampaignsUserGuidePage() {
    const [md, setMd] = useState(null);
    const [err, setErr] = useState(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await client.get("/api/docs/campaigns-user-guide", {
                    params: { format: "markdown" },
                    responseType: "text",
                });
                if (!cancelled) setMd(r.data);
            } catch (e) {
                if (!cancelled) {
                    setErr(e?.response?.data?.detail || e?.message || "Failed to load guide");
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const onDownloadPdf = async () => {
        setDownloading(true);
        try {
            const r = await client.get("/api/docs/campaigns-user-guide", {
                params: { format: "pdf" },
                responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "UAVSimNet_Campaigns_User_Guide.pdf";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 500);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[CampaignsUserGuidePage] PDF download failed", e);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div
            className="campaigns-user-guide-page max-w-4xl mx-auto px-6 py-10"
            data-testid="campaigns-user-guide-page"
        >
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-wider text-ux-text-3">Help</div>
                    <h1
                        className="text-2xl font-semibold text-ux-text-1"
                        data-testid="campaigns-user-guide-title"
                    >
                        UAVSimNet — Campaigns User Guide
                    </h1>
                    <div className="text-sm text-ux-text-3 mt-1">
                        Single-source reference for the unified Campaign Builder.
                    </div>
                </div>
                <Button
                    onClick={onDownloadPdf}
                    disabled={downloading || !md}
                    className="gap-2"
                    data-testid="campaigns-user-guide-download-pdf"
                >
                    {downloading ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}
                    Download PDF
                </Button>
            </div>

            {err && (
                <div
                    className="rounded-md border border-ux-danger/40 bg-ux-danger/5 p-3 text-sm text-ux-danger"
                    data-testid="campaigns-user-guide-error"
                >
                    {err}
                </div>
            )}

            {!md && !err && (
                <div className="flex items-center gap-2 text-ux-text-3 py-12">
                    <Loader2 className="size-4 animate-spin" />
                    Loading guide…
                </div>
            )}

            {md && (
                <article
                    className="prose prose-invert max-w-none campaigns-user-guide-md"
                    data-testid="campaigns-user-guide-body"
                >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {md}
                    </ReactMarkdown>
                </article>
            )}
        </div>
    );
}
